/**
 * The single place a request leaves the browser.
 *
 * Every HTTP repository goes through here so the caller's identity is attached
 * once rather than remembered six times. A repository that built its own
 * `fetch` would work until someone added a seventh, and the screen backed by
 * that one would quietly return an unscoped 401.
 *
 * The identity is a session cookie the browser holds and this code cannot read.
 * `credentials: 'include'` is what attaches it, and it is set here for the same
 * reason the header was: one place, so a seventh repository cannot forget.
 *
 * The `X-PVI-Person` header survives beside it as a development fallback, and
 * it is a fallback in the strict sense — the server prefers the cookie, ignores
 * the header entirely where an identity provider is configured, and refuses to
 * boot in production without one. It is still sent because a checkout with no
 * session yet should show an estate rather than a wall of 401s while the demo
 * sign-in is in flight.
 *
 * `anonymous: true` is for the three sign-on calls themselves. Asking who is
 * signed in must not be answered by "whoever the header says", or the browser
 * would believe it had a session it does not have.
 */

let currentPersonId: string | null = null;

/** Called by `SessionProvider` whenever the signed-in identity changes. */
export function setApiIdentity(personId: string | null): void {
  currentPersonId = personId;
}

export interface ApiFetchInit extends RequestInit {
  /** Send no development identity header. Used by the sign-on calls themselves. */
  anonymous?: boolean;
}

export async function apiFetch(input: string, init?: ApiFetchInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (currentPersonId && !init?.anonymous) headers.set('X-PVI-Person', currentPersonId);
  const response = await fetch(input, {
    ...init,
    headers,
    // The session cookie. Without this the request is sent and the browser
    // simply declines to attach it, which presents as being signed out while
    // the network tab shows every request going out.
    credentials: 'include',
  });

  if (init?.anonymous && response.status === 401) {
    /*
      A 401 from a sign-on call is the answer, not a failure: "nobody is signed
      in" is exactly what the caller asked. Throwing here would make the shell
      report an error on every first visit.
    */
    return response;
  }

  if (response.status === 401) {
    // Surfaced as an error rather than an empty result: a screen that renders
    // "no data" for an authorisation failure tells the reader their estate is
    // empty, which is the wrong thing to believe and the wrong thing to act on.
    throw new Error(
      'This request was not recognised as coming from a signed-in user. Choose an identity and retry.',
    );
  }
  return response;
}
